<template>
  <layout-clean
    class="screener"
    :layoutAlignCenter="!(showFooter && showHeader)"
  >
    <template v-slot:header>
      <header v-if="showHeader" class="screener__fixed-header">
        <img
          class="screener__logo"
          :src="logoUrl"
          v-if="logoUrl && logoUrl.length > 0"
        />
      </header>
    </template>
    <v-container class="screener__form">
      <v-row>
        <v-col cols="12" md="10" lg="8" offset-md="1" offset-lg="2">
          <!-- Step 0 - Welcome Screen to get survey data -->
          <template
            v-if="
              (loadingResultsPrompt && forceNewDialog) ||
                (step === 0 && (forceNewDialog || !fetched))
            "
          >
            <survey-message-section
              @handle-submit="
                () => {
                  onClickStart()
                }
              "
              :title="
                `Congratulations on taking the next step toward better brain health!`
              "
              description="The brain is responsible for everything you think, feel, say, and do and is central to health and well-being. Our global team of experts has collaborated for years to build a comprehensive and straightforward approach to better brain health and optimized brain performance, resulting in the ten protective and five cognitive factors evaluated here. Take the screener, see the results, and learn practices for your best brain life!"
              :showButton="true"
              :isLoading="loading"
              :logoUrl="logoUrl"
              :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
              v-if="!isTheRiseFrameworkSurvey"
            >
              <v-row
                class="text-left mb-3 screener__summary-form"
                v-if="screenerVersion === 2"
              >
                <v-col cols="12" md="10" lg="8" offset-md="1" offset-lg="2">
                  <v-row>
                    <v-col cols="12">
                      <bh-text-input
                        v-model="surveySummaryData.email"
                        higher
                        inputBackgroundClass="bhWhite"
                        roundedClass="rounded"
                        label="Email address"
                        :validate="true"
                        :validationType="'email'"
                        :type="'email'"
                        :no-margin="true"
                        class="screener__summary-form__textInput"
                        @input-text="updateSummaryField('email', $event)"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </survey-message-section>
            <survey-message-section
              @handle-submit="
                () => {
                  onClickStart()
                }
              "
              :title="`Welcome!`"
              description="People slide into different Mind Traps given circumstances but will have a greater tendency toward one or two. We hope you smile as you recognize the Mind Traps in yourself and others... they become apparent quickly, are EVERYWHERE, and once seeing them, you cannot unsee them!"
              :showButton="true"
              :isLoading="loading"
              :logoUrl="logoUrl"
              :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
              v-else
            >
              <p class="mb-8" v-if="isTheRiseFrameworkSurvey">
                <strong
                  >The Rise Framework is the best way to solve for Mind Traps
                  and elevate your offering!</strong
                >
              </p>
              <v-row
                class="text-left mb-3 screener__summary-form"
                v-if="screenerVersion === 2"
              >
                <v-col cols="12" md="10" lg="8" offset-md="1" offset-lg="2">
                  <v-row>
                    <v-col cols="12">
                      <bh-text-input
                        v-model="surveySummaryData.email"
                        higher
                        inputBackgroundClass="bhWhite"
                        roundedClass="rounded"
                        label="Email address"
                        :validate="true"
                        :validationType="'email'"
                        :type="'email'"
                        :no-margin="true"
                        class="screener__summary-form__textInput"
                        @input-text="updateSummaryField('email', $event)"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </survey-message-section>
          </template>
          <!-- Survey steps -->
          <survey-section
            v-else-if="step !== 0 && activeSurveySection !== null"
            :key="activeSurveySection.key + showInputRequiredError ? 1 : 0"
            :title="activeSurveySection.title"
            :description="activeSurveySection.description"
            :id="activeSurveySection.id"
            :completed="activeSurveySection.completed"
            :show-input-required-error.sync="showInputRequiredError"
            :questions.sync="activeSurveySection.questions"
            :disabled-questions.sync="disabledQuestionsIds"
            :icon="activeSurveySection.icon"
            :disabled-answers="disabledAnswers"
            @handleChangeAnswer="handleChangeAnswer"
            :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
          />
          <!-- Survey results page -->
          <template
            v-else-if="
              step !== 0 &&
                activeSurveySection === null &&
                isCompleted &&
                !isSubmitForm &&
                !surveyNotFound &&
                (!submitted || skipSubmitForm)
            "
          >
            <survey-message-section
              :title="
                isTheRiseFrameworkSurvey
                  ? 'Congratulations on completing the survey!'
                  : `Congratulations on completing your brain health assessment!`
              "
              :description="
                isTheRiseFrameworkSurvey
                  ? 'Every single business and person I have ever worked with, including myself, is prone to at least one and often more than one of the Mind Traps, depending on the circumstances. Failure to address these Mind Traps means that every single initiative has a meaningful eroding factor working against the outcome.'
                  : `Included below are your results.`
              "
              :logoUrl="logoUrl"
              :showButton="screenerVersion === 1 && !skipSubmitForm"
              buttonText="Get results"
              :isLoading="loading"
              @handle-submit="showSubmitForm"
              :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
            >
              <survey-feedback
                :title="`Share feedback with Doug Harrison. It’s Appreciated!`"
                v-if="survey.feedback"
                :is-completed="isSurveyFeedbackCompleted"
                :isTheRiseFrameworkSurvey="isTheRiseFrameworkSurvey"
                :questions.sync="survey.feedback"
                @handleChangeFeedbackAnswer="handleChangeFeedbackAnswer"
                :disabled-questions.sync="disabledFeedbackQuestionsIds"
                @submitFeedbackSurvey="answerFeedbackQuestions"
              />
              <v-btn
                v-if="isTheRiseFrameworkSurvey"
                href="https://calendly.com/ceodoug/quick-connect"
                color="mainColor"
                rounded
                elevation="0"
                class="mb-6 bhWhite--text"
                target="_blank"
              >
                Connect with Doug directly
              </v-btn>
              <survey-results-chart
                v-if="surveyResults !== null"
                :results="surveyResults"
                :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
                :show-additional-scores="screenerHasAdditionalScores"
                :additional-scores="surveyAdditionalScores"
              />
            </survey-message-section>
          </template>
          <!-- Personal informations step - collecting personal info -->
          <survey-message-section
            v-else-if="
              step !== 0 &&
                activeSurveySection === null &&
                isCompleted &&
                isSubmitForm &&
                !surveyNotFound &&
                !submitted &&
                !skipSubmitForm
            "
            :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
            @handle-submit="submitForm"
            :title="
              `Hi there!\n\nThanks for taking the time to answer some\nquestions about brain health`
            "
            description="Brain health includes the brain's physiological, electrical, and metabolic health as an organ and the dynamic interaction between the brain, mind, body, and environment. Concentrating, remembering, communicating, learning, maintaining a clear, active mind, and engaging with other systems in your body and the outer world depend on a healthy brain. By completing this screener and examining your results, you are taking a step toward enhanced brain health, optimal brain performance, and the reduction of risk of future brain illness and injury."
            :showButton="true"
            buttonText="Send results"
            :isLoading="loading"
            :logoUrl="logoUrl"
          >
            <v-row class="text-left mb-3 screener__summary-form">
              <v-col cols="12" md="10" lg="8" offset-md="1" offset-lg="2">
                <v-row>
                  <v-col cols="12" sm="6">
                    <bh-text-input
                      v-model="surveySummaryData.firstName"
                      higher
                      inputBackgroundClass="bhWhite"
                      roundedClass="rounded"
                      label="First name"
                      :no-margin="true"
                      class="screener__summary-form__textInput"
                      @input-text="updateSummaryField('firstName', $event)"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <bh-text-input
                      v-model="surveySummaryData.lastName"
                      higher
                      inputBackgroundClass="bhWhite"
                      roundedClass="rounded"
                      label="Last name"
                      :no-margin="true"
                      class="screener__summary-form__textInput"
                      @input-text="updateSummaryField('lastName', $event)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <bh-text-input
                      v-model="surveySummaryData.email"
                      higher
                      inputBackgroundClass="bhWhite"
                      roundedClass="rounded"
                      label="Email address"
                      :validate="true"
                      :validationType="'email'"
                      :type="'email'"
                      :no-margin="true"
                      class="screener__summary-form__textInput"
                      @input-text="updateSummaryField('email', $event)"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </survey-message-section>
          <!-- Thank you page - last step, just for say thank you for submiting -->
          <survey-message-section
            v-else-if="
              step !== 0 &&
                activeSurveySection === null &&
                isCompleted &&
                isSubmitForm &&
                !surveyNotFound &&
                submitted &&
                !skipSubmitForm
            "
            :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
            :title="`Congratulations on completing the screener! Thank you.`"
            :logoUrl="logoUrl"
          />
          <!-- Survey not found or not active -->
          <survey-message-section
            v-else-if="surveyNotFound"
            title="Survey not found or expired."
            description="We are sorry, but survey is not found or expired."
            :logoUrl="logoUrl"
            :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
          />
          <!-- unexpected error -->
          <survey-message-section
            v-else
            title="Unexpected error"
            description="Something went wrong, please try again."
            :logoUrl="logoUrl"
            :is-the-rise-framework-survey="isTheRiseFrameworkSurvey"
          />
        </v-col>
      </v-row>
    </v-container>
    <template v-slot:footer>
      <footer class="screener__fixed-footer" v-if="showFooter">
        <v-row class="align-center section-data">
          <v-col cols="8" md="4" class="d-flex align-center">
            <v-progress-circular
              :rotate="-90"
              :size="40"
              :width="3"
              :value="sectionsData.progress"
              :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
              class="section-data__progress ml-2 mr-4"
            >
              <span class="section-data__progress--currentValue">{{
                sectionsData.currentSection
              }}</span
              >/{{ sectionsData.maxSections }}
            </v-progress-circular>
            <div class="section-data__section-name">
              <h2
                :class="
                  `${
                    isTheRiseFrameworkSurvey ? 'mainColor--text' : ''
                  } section-data__section-name--current-title`
                "
              >
                {{ sectionsData.sectionTitle }}
              </h2>
              <p class="section-data__section-name--next-title">
                <span class="section-data__section-name--prefix">Next:</span>
                {{ sectionsData.nextSectionTitle }}
              </p>
            </div>
          </v-col>
          <v-col cols="4" class="d-flex justify-end justify-md-center">
            <v-btn
              rounded
              text
              class="navigation-arrow"
              @click="prevStep"
              :loading="prevStepButtonLoading"
              :disabled="!sectionsData.canPrevStep"
              :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
            >
              <v-icon
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                >bhi-arrow-left</v-icon
              >
              <span class="d-none ml-3 d-sm-inline">Previous</span>
            </v-btn>
            <v-btn
              rounded
              text
              class="navigation-arrow"
              @click="
                () => {
                  nextStep(false)
                }
              "
              :loading="nextStepButtonLoading"
              :disabled="!sectionsData.canNextStep"
              :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
            >
              <span class="d-none mr-3 d-sm-inline">Next</span>
              <v-icon
                :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
                >bhi-arrow-right</v-icon
              >
            </v-btn>
          </v-col>
          <v-col cols="4" class="d-none d-md-flex justify-end">&nbsp;</v-col>
        </v-row>
      </footer>
    </template>
    <confirm-dialog
      description="Unanswered questions remain that can aid your assessment"
      cancelText="Skip"
      submitText="Answer"
      :showDialog="skipDialog"
      @cancel="skipDialogCancel"
      @submit="skipDialogSubmit"
      :submit-btn-class="isTheRiseFrameworkSurvey ? 'bhWhite--text' : ''"
      :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
    />
    <confirm-dialog
      :maxWidth="420"
      title="You have already filled in the Screener."
      description="Would you like to retake it or see your previous score?"
      cancelText="Retake"
      submitText="Go to results"
      :loading-buttons="loadingResultsPrompt"
      :showDialog="forceNewDialog"
      @cancel="forceNewDialogCancel"
      @submit="forceNewDialogSubmit"
      :submit-btn-class="isTheRiseFrameworkSurvey ? 'bhWhite--text' : ''"
      :color="isTheRiseFrameworkSurvey ? 'mainColor' : 'primary'"
    />
    <public-survey-fun-fact-dialog
      v-if="currentFunFact"
      :title="currentFunFact.title"
      :description="currentFunFact.description"
      :showDialog="funFactsDialog"
      @submit="closeFunFactDialog"
    />
  </layout-clean>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import {
  checkIfAnswered,
  checkIfAnswerIsSelected,
} from '@/helpers/screenerCheckIfAnswered'
import { SURVEY_ANSWER_TYPES, SURVEY_QUESTION_TYPES } from '@/helpers/constants'
import LayoutClean from '@/layouts/LayoutClean.vue'
import SurveyMessageSection from '@/components/survey/SurveyMessageSection.vue'
import SurveySection from '@/components/survey/SurveySection.vue'
import BhTextInput from '@/components/inputs/BhTextInput.vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import emailValidation from '@/helpers/emailValidation.js'
import SurveyResultsChart from '@/components/survey/SurveyResultsChart.vue'
import ConfirmDialog from '../components/dialogs/ConfirmDialog.vue'
import PublicSurveyFunFactDialog from '../components/dialogs/PublicSurveyFunFactDialog.vue'
import SurveyFeedback from '../components/survey/SurveyFeedback.vue'

export default {
  name: 'ScreenerForm', // Survey
  components: {
    LayoutClean,
    SurveyMessageSection,
    SurveySection,
    SurveyResultsChart,
    BhTextInput,
    ConfirmDialog,
    PublicSurveyFunFactDialog,
    SurveyFeedback,
  },
  data: () => ({
    showInputRequiredError: true,
    activeSurveySection: null,
    prevStepIsSurvey: true,
    nextStepIsSurvey: true,
    SURVEY_QUESTION_TYPES,
    SURVEY_ANSWER_TYPES,
    step: 0,
    fetched: false,
    nextStepButtonLoading: false,
    prevStepButtonLoading: false,
    surveySummaryData: {
      firstName: '',
      lastName: '',
      email: '',
    },
    isCompleted: false,
    isSubmitForm: false,
    submitted: false,
    forceNewDialog: false,
    skipDialog: false,
    debouncedPendingAnswers: [], // array for check if there is some answers still saving to API
    funFactsDialog: false,
    funFactsTexts: [
      {
        title: 'Fun fact! 🤓',
        description:
          'The brain only weighs about 3 pounds but uses 20% of the body’s energy!',
        step: 5,
        shown: false,
      },
      {
        title: 'Strange but true. 🤔',
        description:
          'The brain interprets all pain signals, but does not have pain receptors.',
        step: 9,
        shown: false,
      },
      {
        title: 'Brain Power! 🧠💪',
        description:
          'Reworking brain neuroplasticity allows us to respond in new ways to our environment.',
        step: 13,
        shown: false,
      },
    ],
    currentFunFact: null,
    disabledQuestionsIds: [],
    disabledFeedbackQuestionsIds: [],
    feedbackAnswersData: {},
    isSurveyFeedbackCompleted: false,
    loadingResultsPrompt: false,
  }),
  computed: {
    ...mapState('surveyStates', [
      'survey',
      'loading',
      'surveyResults',
      'surveyAdditionalScores',
      'confirmationAnswers',
    ]),
    ...mapGetters('surveyStates', ['getActiveSectionRefs']),
    isTheRiseFrameworkSurvey() {
      return this?.$attrs?.isTheRiseFrameworkSurvey || false
    },
    sectionIcons() {
      if (this.isTheRiseFrameworkSurvey) {
        return [null, null, null]
      }
      return [
        null,
        null,
        null,
        null,
        null,
        require('@/assets/screener-icons/nutrition.png'),
        require('@/assets/screener-icons/general_health.png'),
        require('@/assets/screener-icons/physical_activity.png'),
        require('@/assets/screener-icons/emotional_wellbeing.png'),
        require('@/assets/screener-icons/stress_resilience.png'),
        require('@/assets/screener-icons/sleep.png'),
        require('@/assets/screener-icons/meaning_and_purpose.png'),
        require('@/assets/screener-icons/social_connection.png'),
        require('@/assets/screener-icons/cognitive_stimulation.png'),
        require('@/assets/screener-icons/home_environment.png'),
      ]
    },
    showFooter() {
      return this.activeSurveySection !== null && !this.forceNewDialog
    },
    showHeader() {
      return this.activeSurveySection !== null && !this.forceNewDialog
    },
    disabledAnswers() {
      return (
        this.fetched &&
        this.survey !== null &&
        this.survey?.status === 'COMPLETED'
      )
    },
    surveyNotFound() {
      // Show when there is no survey or status is other than "OPENED"
      return (
        this.fetched &&
        (this.survey === null ||
          (this.survey?.status !== 'OPENED' &&
            this.survey?.status !== 'COMPLETED'))
      )
    },
    sectionsData() {
      const sections = (this.survey?.sections || []).filter(
        el => !el.ref || this.getActiveSectionRefs.includes(el.ref),
      )
      const sectionNumber = this.step > 0 ? this.step - 1 : 0
      const currentSection = sections?.[sectionNumber] || null
      const currentSectionNumber = sectionNumber + 1
      const maxSections = sections.length || 0
      const nextSection = sections?.[sectionNumber + 1] || null

      const canPrevStep =
        !!sections?.[sectionNumber - 1] && !this.prevStepButtonLoading

      return {
        maxSections,
        currentSection: currentSectionNumber,
        progress:
          maxSections === 0 ? 0 : (currentSectionNumber / maxSections) * 100,
        sectionTitle: currentSection?.title || 'No name',
        nextSectionTitle: nextSection?.title || 'Submit form',
        canPrevStep,
        canNextStep:
          !this.nextStepButtonLoading &&
          (nextSection || (currentSection && !nextSection)), // can go next if there is next section with questions, or this is last section and next one should be submit screen
      }
    },
    publicKey() {
      return this?.$attrs?.publicKey || 'XyZh'
    },
    screenerHasAdditionalScores() {
      return this?.$attrs?.screenerHasAdditionalScores || false
    },
    screenerVersion() {
      return this?.$attrs?.screenerVersion || 1
    },
    skipSubmitForm() {
      return this?.$attrs?.skipSubmitForm || false
    },
    logoUrl() {
      if (this.isTheRiseFrameworkSurvey) {
        return require('@/assets/TheRiseFrameworkNavy.png')
      }
      return require('@/assets/brainhealth-logo.svg') // minimum 230px width // @TODO: Change logo and add variable string to get correct tenant logo,
    },
  },
  methods: {
    ...mapActions('surveyStates', [
      'startPublicSurveyForm',
      'answerPublicSurveyForm',
      'answerFeedbackForPublicSurveyForm',
      'completePublicSurveySection',
      'submitPublicSurvey',
      'completePublicSurvey',
    ]),
    ...mapMutations('appStates', ['setSnackbarNotification']),
    ...mapMutations('surveyStates', [
      'setUpdatedAnswers',
      'setUpdatedFeedbackAnswers',
    ]),
    async handleChangeAnswer({
      question,
      answer,
      data = {},
      parentAnswerId = null,
      parentQuestionId = null,
      rootParentAnswerId = null,
      rootParentQuestionId = null,
      allAnswersData,
    }) {
      if (!question || !answer) {
        console.warn('No question or no answer. Update answer cancelled.')
        return
      }

      const { selected = false, content = null, value = null } = data

      const debounceKey = `question-${question.id}`
      if (!this.debouncedPendingAnswers.includes(debounceKey)) {
        this.debouncedPendingAnswers.push(debounceKey)
      }
      debounce(
        () => {
          this.answerQuestion(
            question,
            answer,
            { selected, content, value },
            parentAnswerId,
            parentQuestionId,
            rootParentAnswerId,
            rootParentQuestionId,
            allAnswersData,
          )
        },
        1000,
        debounceKey,
      )
    },
    async handleChangeFeedbackAnswer({
      question,
      answer,
      data = {},
      parentAnswerId = null,
      parentQuestionId = null,
      rootParentAnswerId = null,
      rootParentQuestionId = null,
      allAnswersData,
    }) {
      if (!question || !answer) {
        console.warn('No question or no answer. Update answer cancelled.')
        return
      }

      const { selected = false, content = null, value = null } = data

      this.feedbackAnswersData[question.id] = {
        question,
        answer,
        data: { selected, content, value },
        parentAnswerId,
        parentQuestionId,
        rootParentAnswerId,
        rootParentQuestionId,
        allAnswersData,
      }
    },
    async answerFeedbackQuestions() {
      for (const [_, value] of Object.entries(this.feedbackAnswersData)) {
        this.answerFeedbackQuestion(
          value.question,
          value.answer,
          value.data,
          value.parentAnswerId,
          value.parentQuestionId,
          value.rootParentAnswerId,
          value.rootParentQuestionId,
          value.allAnswersData,
        )
      }
    },
    async answerFeedbackQuestion(
      question,
      answer,
      data,
      parentAnswerId = null,
      parentQuestionId = null,
      rootParentAnswerId = null,
      rootParentQuestionId = null,
      allAnswersData,
    ) {
      // Disable answers in question
      if (
        question?.id &&
        !this.disabledFeedbackQuestionsIds.includes(question?.id)
      )
        this.disabledFeedbackQuestionsIds.push(question.id)
      else if (
        parentQuestionId &&
        !this.disabledFeedbackQuestionsIds.includes(parentQuestionId)
      )
        this.disabledFeedbackQuestionsIds.push(parentQuestionId)
      else if (
        rootParentQuestionId &&
        !this.disabledFeedbackQuestionsIds.includes(rootParentQuestionId)
      )
        this.disabledFeedbackQuestionsIds.push(rootParentQuestionId)

      const answersToChange = []
      const answersIds = Object.keys(allAnswersData)

      //
      answersIds.forEach(answerId => {
        const stateAnswer =
          (question?.answers || []).find(
            stateAnswer => stateAnswer.id === answerId,
          ) || null

        if (stateAnswer) {
          if (stateAnswer?.value !== 0 && !stateAnswer?.value)
            stateAnswer.value = null
          if (!stateAnswer?.content) stateAnswer.content = null
          if (!stateAnswer?.selected) stateAnswer.selected = false

          const isAnswerChanged =
            stateAnswer &&
            (stateAnswer.value !== allAnswersData[answerId].value ||
              stateAnswer.selected !== allAnswersData[answerId].selected ||
              stateAnswer.content !== allAnswersData[answerId].content)

          if (isAnswerChanged) {
            answersToChange.push({
              ...stateAnswer,
              ...allAnswersData[answerId],
            })
          }
        }
      })

      let successComplete = true

      for (const [_, newAnswer] of answersToChange.entries()) {
        try {
          const { id, selected, content, value } = newAnswer

          await this.answerFeedbackForPublicSurveyForm({
            questionId: question.id,
            answerId: id,
            parentAnswerId,
            parentQuestionId,
            rootParentAnswerId,
            rootParentQuestionId,
            selected,
            content,
            value,
            screenerVersion: this.screenerVersion,
          })
        } catch (error) {
          console.log({ error })
          successComplete = false
        }
      }

      this.isSurveyFeedbackCompleted = successComplete

      this.setUpdatedFeedbackAnswers({
        questionId: question.id,
        parentAnswerId,
        parentQuestionId,
        rootParentAnswerId,
        rootParentQuestionId,
        updatedAnswers: [...answersToChange],
      })

      if (
        question?.id &&
        this.disabledFeedbackQuestionsIds.includes(question.id)
      )
        this.disabledFeedbackQuestionsIds = this.disabledFeedbackQuestionsIds.filter(
          el => el !== question.id,
        )
      else if (
        parentQuestionId &&
        this.disabledFeedbackQuestionsIds.includes(parentQuestionId)
      )
        this.disabledFeedbackQuestionsIds = this.disabledFeedbackQuestionsIds.filter(
          el => el !== parentQuestionId,
        )
      else if (
        rootParentQuestionId &&
        this.disabledFeedbackQuestionsIds.includes(rootParentQuestionId)
      )
        this.disabledFeedbackQuestionsIds = this.disabledFeedbackQuestionsIds.filter(
          el => el !== rootParentQuestionId,
        )

      const debounceKey = `feedback-question-${question.id}`
      this.debouncedPendingAnswers = [
        ...this.debouncedPendingAnswers.filter(el => el !== debounceKey),
      ]
    },
    async answerQuestion(
      question,
      answer,
      data,
      parentAnswerId = null,
      parentQuestionId = null,
      rootParentAnswerId = null,
      rootParentQuestionId = null,
      allAnswersData,
    ) {
      // Disable answers in question
      if (question?.id && !this.disabledQuestionsIds.includes(question?.id))
        this.disabledQuestionsIds.push(question.id)
      else if (
        parentQuestionId &&
        !this.disabledQuestionsIds.includes(parentQuestionId)
      )
        this.disabledQuestionsIds.push(parentQuestionId)
      else if (
        rootParentQuestionId &&
        !this.disabledQuestionsIds.includes(rootParentQuestionId)
      )
        this.disabledQuestionsIds.push(rootParentQuestionId)

      const answersToChange = []
      const answersIds = Object.keys(allAnswersData)

      //
      answersIds.forEach(answerId => {
        const stateAnswer =
          (question?.answers || []).find(
            stateAnswer => stateAnswer.id === answerId,
          ) || null

        if (stateAnswer) {
          if (stateAnswer?.value !== 0 && !stateAnswer?.value)
            stateAnswer.value = null
          if (!stateAnswer?.content) stateAnswer.content = null
          if (!stateAnswer?.selected) stateAnswer.selected = false

          const isAnswerChanged =
            stateAnswer &&
            (stateAnswer.value !== allAnswersData[answerId].value ||
              stateAnswer.selected !== allAnswersData[answerId].selected ||
              stateAnswer.content !== allAnswersData[answerId].content)

          if (isAnswerChanged) {
            answersToChange.push({
              ...stateAnswer,
              ...allAnswersData[answerId],
            })
          }
        }
      })

      for (const [_, newAnswer] of answersToChange.entries()) {
        try {
          const { id, selected, content, value } = newAnswer

          await this.answerPublicSurveyForm({
            sectionId: this.activeSurveySection.id,
            questionId: question.id,
            answerId: id,
            parentAnswerId,
            parentQuestionId,
            rootParentAnswerId,
            rootParentQuestionId,
            selected,
            content,
            value,
            screenerVersion: this.screenerVersion,
          })
        } catch (error) {
          console.log({ error })
        }
      }

      this.setUpdatedAnswers({
        sectionId: this.activeSurveySection.id,
        questionId: question.id,
        parentAnswerId,
        parentQuestionId,
        rootParentAnswerId,
        rootParentQuestionId,
        updatedAnswers: [...answersToChange],
      })

      if (question?.id && this.disabledQuestionsIds.includes(question.id))
        this.disabledQuestionsIds = this.disabledQuestionsIds.filter(
          el => el !== question.id,
        )
      else if (
        parentQuestionId &&
        this.disabledQuestionsIds.includes(parentQuestionId)
      )
        this.disabledQuestionsIds = this.disabledQuestionsIds.filter(
          el => el !== parentQuestionId,
        )
      else if (
        rootParentQuestionId &&
        this.disabledQuestionsIds.includes(rootParentQuestionId)
      )
        this.disabledQuestionsIds = this.disabledQuestionsIds.filter(
          el => el !== rootParentQuestionId,
        )

      const debounceKey = `question-${question.id}`
      this.debouncedPendingAnswers = [
        ...this.debouncedPendingAnswers.filter(el => el !== debounceKey),
      ]
      if (this.nextStepButtonLoading) {
        this.retryNextStep()
      }
    },
    async onClickStart(forceNew = false) {
      this.loadingResultsPrompt = true
      const { email } = this.surveySummaryData

      if (this.screenerVersion === 2) {
        const isEmailValid = emailValidation.validEmail(email)

        if (!isEmailValid) {
          this.setSnackbarNotification(
            'E-mail is not valid. Please type valid email.',
          )
          this.loadingResultsPrompt = false
          return
        }
      }

      const newSurvey = await this.startPublicSurveyForm({
        key: this.publicKey,
        forceNew,
        screenerVersion: this.screenerVersion,
        email,
      })

      if (newSurvey?.error === true) {
        this.loadingResultsPrompt = false
        return
      }

      this.fetched = true

      this.loadingResultsPrompt = false
      if (!forceNew && newSurvey?.status === 'COMPLETED') {
        this.forceNewDialog = true
      } else {
        this.nextStep()
      }
    },
    prevStep() {
      this.prevStepButtonLoading = true
      if (this.step > 0) this.step--
      this.prevStepButtonLoading = false
    },
    async nextStep(allowEmpty = false) {
      this.nextStepButtonLoading = true
      let shouldGoNextStep = true
      let shouldShowConfirmDialog = false

      if (this.debouncedPendingAnswers.length > 0) {
        return
      }

      this.showInputRequiredError = false

      let invalidConfirmationAnswers = false
      let noAnswerOnRequiredQuestions = false
      const sectionQuestions = this.activeSurveySection?.questions || []

      if (this.survey.status !== 'COMPLETED') {
        for (const question of sectionQuestions) {
          if (invalidConfirmationAnswers || noAnswerOnRequiredQuestions) break
          if (question.required && !checkIfAnswered(question, true)) {
            noAnswerOnRequiredQuestions = true
            break
          }
          for (const answer of question.answers) {
            if (invalidConfirmationAnswers || noAnswerOnRequiredQuestions) break
            if (
              answer.confirmation &&
              answer.content !== this.confirmationAnswers[answer.id]
            ) {
              invalidConfirmationAnswers = true
              break
            }

            // if answer is selected and has embedded question
            if (
              checkIfAnswerIsSelected(answer, question.type) &&
              answer.embeddedQuestion
            ) {
              if (answer.embeddedQuestion.required) {
                let firstEmbeddedChecked = checkIfAnswered(
                  answer.embeddedQuestion,
                  true,
                )
                if (!firstEmbeddedChecked) {
                  noAnswerOnRequiredQuestions = true
                  break
                } else {
                  if (answer.embeddedQuestion.answers) {
                    for (const firstEmbeddedAnswer of answer.embeddedQuestion
                      .answers) {
                      // If embedded answer selected (1st lvl)
                      if (
                        checkIfAnswerIsSelected(
                          firstEmbeddedAnswer,
                          answer.embeddedQuestion.type,
                        ) &&
                        firstEmbeddedAnswer.embeddedQuestion
                      ) {
                        if (firstEmbeddedAnswer.embeddedQuestion.required) {
                          let secondEmbeddedChecked = checkIfAnswered(
                            firstEmbeddedAnswer.embeddedQuestion,
                            true,
                          )
                          if (!secondEmbeddedChecked) {
                            noAnswerOnRequiredQuestions = true
                            break
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      if (
        this.step !== 0 &&
        (invalidConfirmationAnswers || noAnswerOnRequiredQuestions)
      ) {
        let errorText = 'Some fields have errors.'
        if (invalidConfirmationAnswers && noAnswerOnRequiredQuestions) {
          errorText = 'All answers in this section are required.'
        } else if (invalidConfirmationAnswers) {
          errorText = 'Study ID fields do not match.'
        } else if (noAnswerOnRequiredQuestions) {
          errorText = 'All answers in this section are required.'
        }
        this.setSnackbarNotification(errorText)
        this.showInputRequiredError = true
        this.nextStepButtonLoading = false
        return
      }

      let isSectionCompleted = true
      if (!this.disabledAnswers) {
        if (this.step !== 0 && this.activeSurveySection?.id) {
          isSectionCompleted = await this.completePublicSurveySection({
            sectionId: this.activeSurveySection.id,
            allowEmpty,
            screenerVersion: this.screenerVersion,
          })
          shouldGoNextStep = isSectionCompleted

          if (!allowEmpty && !isSectionCompleted) {
            shouldShowConfirmDialog = true
          }
        }

        if (shouldShowConfirmDialog) {
          this.skipDialog = true
        }
      }

      if (!this.nextStepIsSurvey && !this.isCompleted && shouldGoNextStep) {
        let isSurveyCompleted = await this.completePublicSurvey({
          screenerVersion: this.screenerVersion,
        })
        this.isCompleted = isSurveyCompleted
        shouldGoNextStep = isSurveyCompleted

        if (this.skipSubmitForm) {
          this.submitForm(false)
        }
      }

      if (shouldGoNextStep) {
        this.step++
      }
      this.nextStepButtonLoading = false
    },
    async goToResults() {
      this.loadingResultsPrompt = true
      this.nextStepButtonLoading = true
      const sectionsNumber = this.survey?.sections?.length || 0
      const resultsStepNumber = sectionsNumber + 1

      let isSurveyCompleted = await this.completePublicSurvey({
        screenerVersion: this.screenerVersion,
      })

      this.nextStepButtonLoading = false
      this.forceNewDialog = false

      if (isSurveyCompleted) {
        this.isCompleted = isSurveyCompleted
        this.nextStepIsSurvey = false
        this.step = resultsStepNumber
      } else {
        this.nextStep()
      }
    },
    async retryNextStep() {
      if (this.debouncedPendingAnswers.length === 0) {
        this.nextStep()
      }
    },
    updateSummaryField(fieldName, value) {
      this.surveySummaryData[fieldName] = value
    },
    async submitForm(goNextStep) {
      const goNextStepOnSubmit = goNextStep !== false
      if (!this.skipSubmitForm) {
        const { email } = this.surveySummaryData
        const isEmailValid = emailValidation.validEmail(email)

        if (!isEmailValid) {
          this.setSnackbarNotification(
            'E-mail is not valid. Please type valid email.',
          )
          return false
        }
      }

      const isSubmitted = await this.submitPublicSurvey({
        ...this.surveySummaryData,
        screenerVersion: this.screenerVersion,
        skipSubmitForm: this.skipSubmitForm,
      })
      this.submitted = isSubmitted

      if (isSubmitted && goNextStepOnSubmit) {
        this.nextStep()
      }
    },
    skipDialogCancel() {
      this.skipDialog = false
      this.nextStep(true)
    },
    skipDialogSubmit() {
      this.skipDialog = false
      this.$vuetify.goTo(0)
    },
    showSubmitForm() {
      this.isSubmitForm = true
      this.nextStep()
    },
    forceNewDialogCancel() {
      this.onClickStart(true)
    },
    forceNewDialogSubmit() {
      this.goToResults()
    },
    closeFunFactDialog() {
      this.funFactsDialog = false
      if (this.currentFunFact) {
        const newFunFacts = [...this.funFactsTexts]
        const funFactIndex = newFunFacts.findIndex(
          el => el.step === this.currentFunFact.step,
        )
        newFunFacts[funFactIndex] = {
          ...newFunFacts[funFactIndex],
          shown: true,
        }
        this.funFactsTexts = [...newFunFacts]
      }
    },
  },
  watch: {
    survey: {
      deep: true,
      handler(newSurvey) {
        const sectionNumber = this.step > 0 ? this.step - 1 : 0
        const sections = (newSurvey?.sections || []).filter(
          el => !el.ref || this.getActiveSectionRefs.includes(el.ref),
        )
        const activeSection = sections?.[sectionNumber] || null

        if (activeSection !== null) {
          const timestamp = this.$moment().unix()
          activeSection.key = `active-section-${activeSection.id}-update-${timestamp}`
          if (this.sectionIcons[sectionNumber]) {
            activeSection.icon = this.sectionIcons[sectionNumber]
          }
        }

        this.activeSurveySection = activeSection ? { ...activeSection } : null
      },
    },
    activeSurveySection: {
      deep: true,
      handler(value, prev) {
        if (!!value?.id && value?.id !== prev?.id) {
          // Scroll top on change active survey section
          setTimeout(() => {
            this.$vuetify.goTo(0)
          }, 150)
        }
      },
    },
    step(newStep) {
      const sectionNumber = newStep > 0 ? newStep - 1 : 0
      const nextSectionNumber = sectionNumber + 1
      const sections = (this.survey?.sections || []).filter(
        el => !el.ref || this.getActiveSectionRefs.includes(el.ref),
      )
      const activeSection = sections?.[sectionNumber] || null

      this.nextStepIsSurvey = !!sections?.[nextSectionNumber] || null

      if (activeSection !== null) {
        const timestamp = this.$moment().unix()
        activeSection.key = `active-section-${activeSection.id}-update-${timestamp}`
        if (this.sectionIcons[sectionNumber]) {
          activeSection.icon = this.sectionIcons[sectionNumber]
        }
      }

      this.activeSurveySection = activeSection ? { ...activeSection } : null

      if (this.survey?.status === 'OPENED') {
        const funFact = this.funFactsTexts.find(
          el => !el.shown && el.step >= this.step,
        )
        this.currentFunFact = funFact || null
        if (funFact && funFact?.step === newStep) {
          this.funFactsDialog = true
        }
      }

      // Scroll top on change step
      setTimeout(() => {
        this.$vuetify.goTo(0)
      }, 150)
    },
  },
}
</script>

<style lang="scss">
.screener {
  background: #f4f4f4; // @TODO: Change bg color to variable

  -webkit-print-color-adjust: exact !important; /* Chrome, Safari */
  color-adjust: exact !important; /*Firefox*/

  &__summary-form {
    width: 100%;

    &__textInput {
      .rounded {
        border-radius: $border-radius-root3 !important;
      }
      .bh-text-input {
        &__input {
          padding: 0 24px;
          font-weight: 600;
        }
      }
    }
  }

  &__fixed-header {
    top: 0;
    left: 0;
    padding: 18px;
    @media only screen and (min-width: 1024px) {
      position: sticky;
    }
  }

  &__logo {
    max-width: 175px;
  }

  &__fixed-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: $gutter ($gutter * 1.5);
    background-color: #f4f4f4;
    -webkit-print-color-adjust: exact !important; /* Chrome, Safari */
    color-adjust: exact !important; /*Firefox*/
    border-top: 1px solid $bh-gray--6;
  }

  &__form {
    padding-bottom: 80px;
  }

  .section-data {
    &__progress {
      font-size: 8px;
      line-height: 40px;

      &--currentValue {
        font-size: 16px;
      }
    }

    &__section-name {
      &--current-title {
        font-size: 14px;
        margin-bottom: 0;
      }
      &--prefix {
        color: #999999;
      }
      &--next-title {
        font-size: 12px;
        margin-bottom: 0;
      }
    }
  }

  @media (max-width: 599px) {
    .navigation-arrow {
      padding-left: 0 !important;
      padding-right: 0 !important;
      min-width: 48px !important;
    }
  }
}
</style>
